<script>
import {
  computed, defineComponent, ref, watch,
} from '@vue/composition-api';
import { format } from 'date-fns';
import DashboardData from './DashboardData.vue';
import CampaignProgress from './DashboardCampaignProgress.vue';
import DashboardStoreSelect from './DashboardStoreSelect.vue';
import DashboardStoreExpansionalPanel from './DashboardStoreExpansionalPanel.vue';
import { useManagerDashboard } from '../../composables/useManagerDashboard';
import { useCampaignsStats } from '../../composables/useCampaignStats';
import { useZipperRevenue } from '../../composables/useZipperRevenue';
import { useClientsContacted } from '../../composables/useClientsContacted';
import { useVuexSellerStores } from '../../composables/useVuexSellerStores';

export default defineComponent({
  components: {
    DashboardData,
    CampaignProgress,
    DashboardStoreSelect,
    DashboardStoreExpansionalPanel,
  },
  setup(props, { root }) {
    const {
      isCampaignDialogOpen,
      toggleCampaignDialog,
      currentCampaign,
      storeSelectedCampaignResults: storeSelected,
      currentRevenue,
      changeStoreSelectedCampaignResults,
      formatNumber,
    } = useManagerDashboard();
    const { stats } = useCampaignsStats();
    const customer = computed(() => root.$store.state.customer);
    const { load: loadZipperRevenue, revenue, loading: loadingZipperRevenue } = useZipperRevenue();
    const {
      load: loadClientsContacted,
      clientsContacted: allClientsContacted,
      loading: loadingClientsContacted,
    } = useClientsContacted();
    const { stores: sellerStores, loading: loadingStores } = useVuexSellerStores(root);

    const clientsContacted = computed(() => {
      if (allClientsContacted.value && allClientsContacted.value.length > 0) {
        return allClientsContacted.value[0];
      }
      return { total: 0 };
    });

    const formatDate = (d) => format(d, 'DD/MM/YYYY');

    const campaignResultsByStore = computed(() => {
      if (stats.value && stats.value.length > 0) {
        const found = stats.value.find((el) => el[0].campaignId === currentCampaign.value.id);
        if (found) {
          const statsByStore = storeSelected.value.id
            .map((storeId) => {
              const filteredStores = found.filter((el) => el.storeId === storeId);
              if (filteredStores && filteredStores.length > 0) {
                return filteredStores.reduce((sum, val) => ({
                  storeId: val.storeId,
                  store: val.store,
                  sent: sum.sent + val.sent,
                  total: sum.total + val.total,
                }));
              }
              return null;
            })
            .filter((el) => !!el);
          return [...statsByStore];
        }
      }
      return [];
    });

    const stores = computed(() => {
      if (stats.value && stats.value.length > 0) {
        const found = stats.value.find((el) => el[0].campaignId === currentCampaign.value.id);
        if (found) {
          const campaignStoreIds = found.map((c) => c.storeId);
          return sellerStores.value.filter((el) => campaignStoreIds.includes(el.id));
        }
      }
      return [];
    });

    const totalCampaignResults = computed(() => {
      if (campaignResultsByStore.value && campaignResultsByStore.value.length > 0) {
        return campaignResultsByStore.value.reduce((a, b) => ({
          sent: a.sent + b.sent,
          total: a.total + b.total,
        }));
      }
      return [];
    });

    const totalRevenueThisMonthByCampaign = computed(() => {
      if (revenue.value && revenue.value.length > 0) {
        const found = revenue.value[0];
        if (found) return found;
      }
      return { total: 0, sent: 0 };
    });

    const load = async () => {
      Promise.all([
        loadZipperRevenue(customer.value.id, {
          storeIds: storeSelected.value.id,
          campaignIds: [currentCampaign.value.id],
        }),
        loadClientsContacted(customer.value.id, {
          storeIds: storeSelected.value.id,
          campaignIds: [currentCampaign.value.id],
        }),
      ]);
    };

    watch(storeSelected, async () => {
      if (storeSelected.value) {
        await load();
      }
    });

    return {
      stores,
      storeSelected,
      changeStoreSelectedCampaignResults,
      loadingStores,
      formatNumber,
      currentCampaign,
      isCampaignDialogOpen,
      toggleCampaignDialog,
      totalCampaignResults,
      campaignResultsByStore,
      clientsContacted,
      loadingClientsContacted,
      loadingZipperRevenue,
      currentRevenue,
      totalRevenueThisMonthByCampaign,
      formatDate,
    };
  },
});
</script>

<template>
  <v-dialog
    :value="isCampaignDialogOpen"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    class="campaign-result-dialog"
  >
    <div class="campaign-result-dialog__container">
      <v-layout
        row
        wrap
        style="align-items: center; margin-top: 100px"
      >
        <v-btn
          flat
          icon
          color="primary"
          @click="toggleCampaignDialog"
        >
          <v-icon>
            $vuetify.icons.arrowLeftIcon
          </v-icon>
        </v-btn>
        <v-btn
          flat
          icon
          color="primary"
          style="text-transform: none"
          @click="toggleCampaignDialog"
        >
          Voltar
        </v-btn>
      </v-layout>
      <div class="campaign-result-dialog__container__padding">
        <DashboardStoreSelect
          :change-store-selected="changeStoreSelectedCampaignResults"
          :loading="loadingStores"
          :stores="stores"
          :store-selected="storeSelected"
        />
        <v-flex auto>
          <div
            class="campaign-result-dialog__header"
            text-xs-center
          >
            {{ currentCampaign.name }}
            <v-chip
              class="campaign-result-dialog__status-chip"
              :color="(currentCampaign.status && currentCampaign.status === 'COMPLETE') ? '#6ADDC8' : '#ECF6AA'"
              text-xs-center
              disabled
              text-color="primary"
            >
              {{
                currentCampaign.status && currentCampaign.status === "COMPLETE" ? "Completa" : "Ativa"
              }}
            </v-chip>
          </div>
          <p class="campaign-result-dialog__sub-header">
            Inicio {{ formatDate(currentCampaign.begin) }} Fim {{ formatDate(currentCampaign.end) }}
          </p>
        </v-flex>
        <DashboardData
          :icon="'$vuetify.icons.crmLogoIcon'"
          :subtitle="`de vendas CRM360 até o momento`"
          :loading="loadingZipperRevenue"
          is-main
          :pre-subtitle="
            `${
              totalRevenueThisMonthByCampaign.total
                ? formatNumber((totalRevenueThisMonthByCampaign.total / currentRevenue.total) * 100)
                : 0
            }%`
          "
          :title="`R$ ${totalRevenueThisMonthByCampaign.total}`"
          :tooltip-text="'Resultados baseados nas datas fim de campanhas'"
        />
        <DashboardData
          :icon="'fas fa-funnel-dollar'"
          :subtitle="'de taxa de conversão até o momento'"
          :loading="loadingZipperRevenue"
          is-main
          :title="
            `${
              totalRevenueThisMonthByCampaign.total
                ? formatNumber(
                  (totalRevenueThisMonthByCampaign.conversions / totalCampaignResults.sent) * 100
                )
                : 0
            }%`
          "
        />
        <DashboardData
          :icon="'fas fa-user-plus'"
          :loading="loadingClientsContacted"
          is-main
          :subtitle="'clientes únicos contatados até o momento'"
          :title="`${formatNumber(clientsContacted.total)}`"
        />
        <div v-show="storeSelected && storeSelected.id && storeSelected.id.length > 1">
          <CampaignProgress
            title="Total de mensagens enviadas"
            :total-contacts="totalCampaignResults.total"
            :contacts-sent="totalCampaignResults.sent"
          />
        </div>
        <CampaignProgress
          v-for="campaignResultByStore in campaignResultsByStore"
          :key="campaignResultByStore.store"
          :title="campaignResultByStore.store"
          :total-contacts="campaignResultByStore.total"
          :contacts-sent="campaignResultByStore.sent"
        />
        <div class="campaign-result-dialog__stores">
          <v-divider />
          <h1 class="campaign-result-dialog__stores__title">
            Envios por vendedor
          </h1>
          <DashboardStoreExpansionalPanel />
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
div.v-dialog__content.v-dialog__content--active {
  z-index: 2 !important;
}
.campaign-result-dialog {
  &__container {
    min-height: 100vh !important;
    background-color: white;
    &__padding {
      padding: 0px 30px 200px 30px !important;
    }
  }
  &__toolbar-title {
    color: #000000;
    font-size: 10pt !important;
  }
  &__header {
    font-family: 'Nunito Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;
  }
  &__sub-header {
    font-family: 'Nunito Sans';
    font-style: normal;
    font-weight: normal;
    font-size: 8px;
    line-height: 11px;
    color: #140C22;
    margin: 0;
    margin-bottom: 24pt;
  }
  &__stores {
    margin: 20pt 0 0 0;
    &__title {
      margin: 20pt 0 16pt 0;
      font-family: 'Nunito Sans';
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 22px;
    }
  }
  &__status-chip {
    color: 'primary';
    justify-content: center;
    width: 70.83px;
    height: 19.82px;
    font-family: 'Neue Metana';
    font-style: normal;
    font-weight: normal;
    font-size: 10px;
    line-height: 7px;
  }
}
::v-deep .p {
    margin-bottom: 0px !important;
  }
</style>
